import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/shadcn";
import { PlusIcon } from "@radix-ui/react-icons";
import { AppButton, AppInput, FigmaTypography, isEnterSubmit } from "@libs/zapi-ui/custom";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Field, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import * as DsMenu from "@libs/zapi-ui/ds/components/dropdown-menu";
import * as DsDialog from "@libs/zapi-ui/ds/components/dialog";
import { ACCEPTED_FILE_TYPES, MAX_FILES, MAX_TOTAL_SIZE_BYTES } from "../../const/repositoryConsts";
import { FileArrowUpIcon, GlobeIcon, PlusIcon as PlusIconDs } from "@phosphor-icons/react";
import { RichMenuItem } from "@/zapi-frontend/widgets/Header/ui/blocks/RichMenu";
import { ChangeEvent, useCallback, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { validateFilesLimit } from "@/zapi-frontend/shared/lib/validateFilesLimit";
import { isValidUrl, normalizeUrlForComparison } from "@/zapi-frontend/shared/utils/url";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

type TUploadFilesButtonProps = {
  createDocument: (file: File) => Promise<void>;
  createWebLink: (url: string) => Promise<void>;
  setUploadingDocuments: (files: string[]) => void;
  isDocumentsPending?: boolean;
  isDocumentsProcessing?: boolean;
  existingWebUrls?: (string | null | undefined)[];
  variant?: "fab" | "header";
  size?: "default" | "sm";
  appearance?: "default" | "design-system";
};

export function UploadFilesButton(props: TUploadFilesButtonProps) {
  const {
    createDocument,
    createWebLink,
    setUploadingDocuments,
    isDocumentsPending,
    isDocumentsProcessing,
    existingWebUrls = [],
    variant = "fab",
    size = "default",
    appearance = "default"
  } = props;
  const { t } = useTranslation(["projectHome", "common"]);
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const attachmentFileInputRef = useRef<HTMLInputElement>(null);
  const [webLinkDialogOpen, setWebLinkDialogOpen] = useState(false);
  const [webLinkUrl, setWebLinkUrl] = useState("");
  const [isAddingWebLink, setIsAddingWebLink] = useState(false);
  const webLinkInputId = useId();

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const files = Array.from(e.target.files);

        const errorMessage = validateFilesLimit({
          files,
          maxFiles: MAX_FILES,
          maxTotalSizeBytes: MAX_TOTAL_SIZE_BYTES
        });
        if (errorMessage) {
          toast.error(errorMessage);

          if (attachmentFileInputRef.current) {
            attachmentFileInputRef.current.value = "";
          }

          return;
        }

        setUploadingDocuments(files.map((f) => f.name));

        try {
          await Promise.all(files.map(createDocument));
        } finally {
          setUploadingDocuments([]);
          // reset the input so the same file can be re‑uploaded if needed
          if (attachmentFileInputRef.current) {
            attachmentFileInputRef.current.value = "";
          }
        }
      }
    },
    [createDocument, setUploadingDocuments]
  );

  const handleAddWebLink = useCallback(async () => {
    if (!webLinkUrl.trim()) return;

    let url = webLinkUrl.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = `https://${url}`;
    }

    if (!isValidUrl(url)) {
      toast.error(t("vaultPage.invalidUrl"));
      return;
    }

    const normalizedNew = normalizeUrlForComparison(url);
    const isDuplicate = existingWebUrls.some((u) => u && normalizeUrlForComparison(u) === normalizedNew);
    if (isDuplicate) {
      toast.error(t("vaultPage.duplicateLink"));
      return;
    }

    setIsAddingWebLink(true);
    try {
      await createWebLink(url);
      setWebLinkDialogOpen(false);
      setWebLinkUrl("");
      toast.success(t("vaultPage.webLinkAdded"));
    } catch {
      toast.error(t("vaultPage.webLinkFailed"));
    } finally {
      setIsAddingWebLink(false);
    }
  }, [webLinkUrl, createWebLink, existingWebUrls, t]);

  const isBusy = isDocumentsPending || isDocumentsProcessing || !hasEdit;

  const uploadHint = isBusy
    ? t("vaultPage.waitForUploads")
    : t("vaultPage.uploadLimit", { maxFiles: MAX_FILES, maxMb: MAX_TOTAL_SIZE_BYTES / 1024 / 1024 });

  const closeWebLinkDialog = () => {
    setWebLinkDialogOpen(false);
    setWebLinkUrl("");
  };

  const handleWebLinkDialogOpenChange = (open: boolean) => {
    if (!isAddingWebLink) {
      setWebLinkDialogOpen(open);
      if (!open) setWebLinkUrl("");
    }
  };

  const fileInput = (
    <input
      type="file"
      ref={attachmentFileInputRef}
      multiple
      accept={ACCEPTED_FILE_TYPES}
      style={{ display: "none" }}
      onChange={handleFileChange}
      data-testid="upload-files-input"
    />
  );

  if (appearance === "design-system") {
    return (
      <>
        <DsMenu.DropdownMenu>
          <DsMenu.DropdownMenuTrigger
            data-testid="file-item-upload-files-fab"
            className={variant === "fab" ? "mt-auto ml-auto mb-2 mr-2" : undefined}
            render={
              variant === "header" ? (
                <Button type="button" shape="pill" disabled={isBusy} data-testid="upload-files-fab">
                  <PlusIconDs data-icon="inline-start" weight="bold" />
                  {t("vaultPage.add")}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="icon"
                  className="rounded-full p-0"
                  disabled={isBusy}
                  data-testid="upload-files-fab"
                >
                  <PlusIcon width={20} height={20} />
                </Button>
              )
            }
          />
          <DsMenu.DropdownMenuContent align="end" sideOffset={8} className="w-72 rounded-xl p-1.5">
            <RichMenuItem
              icon={FileArrowUpIcon}
              title={t("vaultPage.files")}
              description={t("vaultPage.filesLimit", {
                maxFiles: MAX_FILES,
                maxMb: MAX_TOTAL_SIZE_BYTES / 1024 / 1024
              })}
              onClick={() => attachmentFileInputRef.current?.click()}
            />
            <RichMenuItem
              icon={GlobeIcon}
              title={t("vaultPage.webLink")}
              description={t("vaultPage.webLinkDescription")}
              onClick={() => setWebLinkDialogOpen(true)}
            />
          </DsMenu.DropdownMenuContent>
        </DsMenu.DropdownMenu>

        {fileInput}

        <DsDialog.Dialog open={webLinkDialogOpen} onOpenChange={handleWebLinkDialogOpenChange}>
          <DsDialog.DialogContent closeLabel={t("common:close")}>
            <DsDialog.DialogHeader>
              <DsDialog.DialogTitle>{t("vaultPage.addWebLink")}</DsDialog.DialogTitle>
            </DsDialog.DialogHeader>
            <Field className="gap-2">
              <FieldLabel htmlFor={webLinkInputId}>URL</FieldLabel>
              <Input
                id={webLinkInputId}
                data-testid="add-web-link-url-input"
                placeholder="https://example.com"
                value={webLinkUrl}
                onChange={(e) => setWebLinkUrl(e.target.value)}
                disabled={isAddingWebLink}
                onKeyDown={(e) => {
                  if (isEnterSubmit(e, { allowShift: true })) handleAddWebLink();
                }}
              />
            </Field>
            <DsDialog.DialogFooter>
              <Button
                type="button"
                variant="outline"
                data-testid="add-web-link-cancel-button"
                onClick={closeWebLinkDialog}
                disabled={isAddingWebLink}
              >
                {t("common:cancel")}
              </Button>
              <Button
                type="button"
                data-testid="add-web-link-add-button"
                onClick={handleAddWebLink}
                disabled={isAddingWebLink || !webLinkUrl.trim()}
              >
                {isAddingWebLink ? (
                  <>
                    <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                    {t("common:loading")}
                  </>
                ) : (
                  t("vaultPage.add")
                )}
              </Button>
            </DsDialog.DialogFooter>
          </DsDialog.DialogContent>
        </DsDialog.Dialog>
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          asChild
          data-testid="file-item-upload-files-fab"
          className={variant === "fab" ? "mt-auto ml-auto mb-2 mr-2" : undefined}
        >
          {variant === "header" ? (
            <AppButton
              size={size}
              className={size === "sm" ? "gap-1 has-[>svg]:px-2 text-[14px] leading-[13px]" : undefined}
              disabled={isBusy}
              startAdornment={<PlusIcon className={size === "sm" ? "size-3" : "size-4"} />}
              data-testid="upload-files-fab"
            >
              {t("vaultPage.add")}
            </AppButton>
          ) : (
            <AppButton
              size="icon"
              className="rounded-full p-0"
              disabled={isBusy}
              startAdornment={<PlusIcon width={20} height={20} />}
              data-testid="upload-files-fab"
            />
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent className="px-1 mr-5">
          <Tooltip>
            <TooltipTrigger className="w-full">
              <DropdownMenuItem
                onClick={() => attachmentFileInputRef.current?.click()}
                variant="default"
                className="cursor-pointer font-medium px-3 py-2"
                data-testid="upload-files-item"
              >
                <FileArrowUpIcon className="w-4 h-4 text-black" />
                <FigmaTypography variant="label-3">{t("vaultPage.files")}</FigmaTypography>
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent>
              <FigmaTypography variant="body-3">{uploadHint}</FigmaTypography>
            </TooltipContent>
          </Tooltip>
          <DropdownMenuItem
            onClick={() => setWebLinkDialogOpen(true)}
            variant="default"
            className="cursor-pointer font-medium px-3 py-2"
            data-testid="add-web-link-item"
          >
            <GlobeIcon className="w-4 h-4 text-black" />
            <FigmaTypography variant="label-3">{t("vaultPage.webLink")}</FigmaTypography>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {fileInput}

      <Dialog open={webLinkDialogOpen} onOpenChange={handleWebLinkDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("vaultPage.addWebLink")}</DialogTitle>
          </DialogHeader>
          <AppInput
            data-testid="add-web-link-url-input"
            label="URL"
            placeholder="https://example.com"
            value={webLinkUrl}
            onChange={(e) => setWebLinkUrl(e.target.value)}
            disabled={isAddingWebLink}
            onKeyDown={(e) => {
              if (isEnterSubmit(e, { allowShift: true })) handleAddWebLink();
            }}
          />
          <DialogFooter>
            <AppButton
              data-testid="add-web-link-cancel-button"
              appearance="outlined"
              color="zinc150"
              onClick={closeWebLinkDialog}
              disabled={isAddingWebLink}
            >
              {t("common:cancel")}
            </AppButton>
            <AppButton
              data-testid="add-web-link-add-button"
              onClick={handleAddWebLink}
              isLoading={isAddingWebLink}
              disabled={!webLinkUrl.trim()}
            >
              {t("vaultPage.add")}
            </AppButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
